import React, { useState, useEffect } from 'react';
import ReactDOM from 'react-dom';
import styled from 'styled-components';
import VipIcon from '../Icons/VipIcons';
import { PrimaryButton, SecondaryButton, IconButton, UnstyledButton } from './Button';
import {
  startOfMonth, endOfMonth, startOfWeek, endOfWeek,
  eachDayOfInterval, format, isValid, isSameDay, addMonths, subMonths, startOfDay,
} from 'date-fns';

const WEEKDAYS = ['Su', 'Mo', 'Tu', 'We', 'Th', 'Fr', 'Sa'];

function toDateString(date) {
  return format(date, 'yyyy-MM-dd');
}

// ─── Styled Components ────────────────────────────────────────────────────────

const Overlay = styled.div`
  position: fixed;
  inset: 0;
  background: rgba(0, 0, 0, 0.55);
  display: flex;
  align-items: center;
  justify-content: center;
  z-index: 10000200;
  padding: 16px;
`;

const Panel = styled.div`
  background: ${({ theme }) => theme.colors.bg || '#fff'};
  border-radius: 18px;
  box-shadow: 0 24px 64px rgba(0, 0, 0, 0.4);
  padding: 24px;
  user-select: none;
  max-width: 640px;
  width: 100%;
`;

const PanelTitle = styled.h3`
  margin: 0 0 20px;
  font-size: 16px;
  font-weight: 600;
  color: ${({ theme }) => theme.colors.text};
`;

const MonthsRow = styled.div`
  display: flex;
  gap: 32px;

  @media (max-width: 560px) {
    flex-direction: column;
    gap: 20px;
  }
`;

const MonthPanel = styled.div`
  flex: 1;
  min-width: 0;
`;

const CalHeader = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  margin-bottom: 8px;
`;

const MonthTitle = styled.span`
  font-size: 13px;
  font-weight: 600;
  color: ${({ theme }) => theme.colors.text};
`;

const NavBtn = styled(IconButton).attrs({ $size: 'small' })`
  &:disabled {
    opacity: 0.2;
    cursor: default;
  }
`;

const WeekRow = styled.div`
  display: grid;
  grid-template-columns: repeat(7, 1fr);
  margin-bottom: 4px;
`;

const WDLabel = styled.span`
  text-align: center;
  font-size: 10px;
  font-weight: 600;
  color: ${({ theme }) => theme.colors.textSecondary};
  padding: 2px 0;
`;

const DaysGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(7, 1fr);
`;

const DayCell = styled.div`
  position: relative;
  height: 34px;
  display: flex;
  align-items: center;
  justify-content: center;
`;

const RangeBar = styled.div`
  position: absolute;
  top: 4px;
  bottom: 4px;
  left: ${({ $isStart }) => $isStart ? '50%' : '0'};
  right: ${({ $isEnd }) => $isEnd ? '50%' : '0'};
  background: ${({ theme }) => `${theme.colors.primary}22`};
  pointer-events: none;
`;

const DayBtn = styled(UnstyledButton)`
  position: relative;
  z-index: 1;
  width: 30px;
  height: 30px;
  border: none;
  border-radius: 50%;
  background: ${({ $selected, $today, theme }) =>
    $selected
      ? theme.colors.primary
      : $today
      ? `${theme.colors.primary}25`
      : 'transparent'};
  color: ${({ $selected, $otherMonth, theme }) =>
    $selected ? '#fff' : $otherMonth ? theme.colors.textSecondary : theme.colors.text};
  font-size: 12px;
  font-weight: ${({ $selected, $today }) => ($selected || $today ? '700' : '400')};
  cursor: pointer;
  opacity: ${({ $otherMonth }) => ($otherMonth ? 0.35 : 1)};
  transition: background 0.12s;

  &:hover {
    background: ${({ $selected, theme }) =>
      $selected ? theme.colors.primary : theme.colors.bg3};
  }
`;

const Divider = styled.div`
  height: 1px;
  background: ${({ theme }) => theme.colors.divider || theme.colors.border};
  margin: 20px 0 16px;
`;

const RangeLabel = styled.div`
  font-size: 13px;
  color: ${({ theme }) => theme.colors.textMuted};
  text-align: center;
  margin-bottom: 16px;
  min-height: 20px;
`;

const Highlight = styled.span`
  font-weight: 600;
  color: ${({ theme }) => theme.colors.text};
`;

const FooterRow = styled.div`
  display: flex;
  gap: 10px;
  justify-content: flex-end;
`;

// ─── Calendar Month ───────────────────────────────────────────────────────────

function CalendarMonth({ month, from, to, hoverDay, onDayClick, onDayHover, showPrev, showNext, onPrev, onNext }) {
  const days = eachDayOfInterval({
    start: startOfWeek(startOfMonth(month)),
    end: endOfWeek(endOfMonth(month)),
  });

  const today = startOfDay(new Date());

  // Effective end for preview range
  const effectiveEnd = to || hoverDay;
  const rangeStart = from && effectiveEnd ? (from <= effectiveEnd ? from : effectiveEnd) : null;
  const rangeEnd   = from && effectiveEnd ? (from <= effectiveEnd ? effectiveEnd : from) : null;
  const singleDay  = rangeStart && rangeEnd && isSameDay(rangeStart, rangeEnd);

  const isInRange  = (d) => rangeStart && rangeEnd && d > rangeStart && d < rangeEnd;
  const isStart    = (d) => rangeStart && isSameDay(d, rangeStart);
  const isEnd      = (d) => rangeEnd   && isSameDay(d, rangeEnd);
  const isSelected = (d) => (from && isSameDay(d, from)) || (to && isSameDay(d, to));
  const showBar    = (d) => isInRange(d) || (!singleDay && (isStart(d) || isEnd(d)));

  return (
    <MonthPanel>
      <CalHeader>
        <NavBtn onClick={onPrev} disabled={!showPrev} aria-label="Previous month">
          <VipIcon name="ChevronLeft" size={16} />
        </NavBtn>
        <MonthTitle>{format(month, 'MMMM yyyy')}</MonthTitle>
        <NavBtn onClick={onNext} disabled={!showNext} aria-label="Next month">
          <VipIcon name="ChevronRight" size={16} />
        </NavBtn>
      </CalHeader>

      <WeekRow>
        {WEEKDAYS.map(d => <WDLabel key={d}>{d}</WDLabel>)}
      </WeekRow>

      <DaysGrid>
        {days.map((day, i) => {
          const otherMonth = day.getMonth() !== month.getMonth();
          const bar = showBar(day);
          return (
            <DayCell key={i}>
              {bar && (
                <RangeBar
                  $isStart={isStart(day)}
                  $isEnd={isEnd(day)}
                />
              )}
              <DayBtn
                $selected={isSelected(day)}
                $today={isSameDay(day, today)}
                $otherMonth={otherMonth}
                onClick={() => onDayClick(day)}
                onMouseEnter={() => onDayHover(day)}
              >
                {day.getDate()}
              </DayBtn>
            </DayCell>
          );
        })}
      </DaysGrid>
    </MonthPanel>
  );
}

// ─── Main Export ──────────────────────────────────────────────────────────────

export default function DateRangePickerModal({ isOpen, onClose, onConfirm, onClear, initialFrom, initialTo }) {
  const [leftMonth, setLeftMonth] = useState(() => startOfMonth(new Date()));
  const [from, setFrom] = useState(null);
  const [to, setTo] = useState(null);
  const [hoverDay, setHoverDay] = useState(null);

  const rightMonth = addMonths(leftMonth, 1);

  useEffect(() => {
    if (!isOpen) return;
    const f = initialFrom ? startOfDay(new Date(initialFrom + 'T00:00:00')) : null;
    const t = initialTo  ? startOfDay(new Date(initialTo  + 'T00:00:00')) : null;
    setFrom(f);
    setTo(t);
    setHoverDay(null);
    setLeftMonth(f ? startOfMonth(f) : startOfMonth(new Date()));
  }, [isOpen]); // eslint-disable-line react-hooks/exhaustive-deps

  const handleDayClick = (day) => {
    const d = startOfDay(day);
    if (!from || (from && to)) {
      setFrom(d);
      setTo(null);
    } else {
      if (d < from) {
        setTo(from);
        setFrom(d);
      } else {
        setTo(d);
      }
    }
  };

  const handleConfirm = () => {
    if (from && to) onConfirm({ from: toDateString(from), to: toDateString(to) });
  };

  const handleClear = () => {
    setFrom(null);
    setTo(null);
    onClear?.();
  };

  const rangeLabel = () => {
    const fromValid = from && isValid(from);
    const toValid   = to   && isValid(to);
    if (fromValid && toValid) return <>{<Highlight>{format(from, 'MMM d, yyyy')}</Highlight>} — {<Highlight>{format(to, 'MMM d, yyyy')}</Highlight>}</>;
    if (fromValid)            return <>From <Highlight>{format(from, 'MMM d, yyyy')}</Highlight> — select end date</>;
    return 'Select a start date';
  };

  if (!isOpen) return null;

  return ReactDOM.createPortal(
    <Overlay onClick={onClose}>
      <Panel onClick={(e) => e.stopPropagation()}>
        <PanelTitle>Select date range</PanelTitle>

        <MonthsRow onMouseLeave={() => setHoverDay(null)}>
          <CalendarMonth
            month={leftMonth}
            from={from}
            to={to}
            hoverDay={!to ? hoverDay : null}
            onDayClick={handleDayClick}
            onDayHover={setHoverDay}
            showPrev
            showNext={false}
            onPrev={() => setLeftMonth(m => subMonths(m, 1))}
            onNext={null}
          />
          <CalendarMonth
            month={rightMonth}
            from={from}
            to={to}
            hoverDay={!to ? hoverDay : null}
            onDayClick={handleDayClick}
            onDayHover={setHoverDay}
            showPrev={false}
            showNext
            onPrev={null}
            onNext={() => setLeftMonth(m => addMonths(m, 1))}
          />
        </MonthsRow>

        <Divider />
        <RangeLabel>{rangeLabel()}</RangeLabel>

        <FooterRow>
          <SecondaryButton onClick={handleClear}>Clear</SecondaryButton>
          <SecondaryButton onClick={onClose}>Cancel</SecondaryButton>
          <PrimaryButton onClick={handleConfirm} disabled={!from || !to}>Apply</PrimaryButton>
        </FooterRow>
      </Panel>
    </Overlay>,
    document.body
  );
}
